import { Badge, Card, EmptyState, ErrorState, LoadingRows } from '@shared/ui';

import { groupModules } from '../model/navigation';

import type { useBulkModules } from '../hooks/use-bulk-migrations';

/** Step 1: which module to migrate, grouped by the domain that owns it. */
export function ModulePicker({
    query,
    onPick,
}: {
    query: ReturnType<typeof useBulkModules>;
    onPick: (moduleId: string) => void;
}) {
    if (query.isPending) return <LoadingRows label="Loading migration modules…" />;
    if (query.isError) {
        return (
            <Card>
                <ErrorState error={query.error} onRetry={() => void query.refetch()} />
            </Card>
        );
    }
    if (query.data.length === 0) {
        return (
            <Card>
                <EmptyState
                    title="No migration modules"
                    description="The migration runner in this environment has no module registered."
                />
            </Card>
        );
    }

    return (
        <div className="flex flex-col gap-6">
            {groupModules(query.data).map((group) => (
                <section key={group.domain}>
                    <h2 className="mb-3 text-[12px] font-semibold uppercase tracking-wide text-content-muted">
                        {group.domain}
                    </h2>
                    <div className="grid grid-cols-[repeat(auto-fill,minmax(260px,1fr))] gap-4">
                        {group.modules.map((module) => (
                            <button
                                key={module.moduleId}
                                type="button"
                                onClick={() => {
                                    onPick(module.moduleId);
                                }}
                                className="rounded-xl border border-line bg-surface p-4 text-left shadow-card transition-colors hover:border-accent-line hover:bg-surface-sunken"
                            >
                                <div className="flex items-center gap-2">
                                    <span className="text-[15px] font-semibold text-content">
                                        {module.title}
                                    </span>
                                    {module.supportsPreview && <Badge tone="info">Preview</Badge>}
                                </div>
                                <p className="mt-1.5 text-[13px] leading-relaxed text-content-muted">
                                    {module.description}
                                </p>
                                <p className="mt-2 font-mono text-[11.5px] text-content-faint">
                                    {module.moduleId}
                                </p>
                            </button>
                        ))}
                    </div>
                </section>
            ))}
        </div>
    );
}
